/**
 * CR168: Discovery Results Dialog
 * Shows flyer bucket discovery results with eligibility categorization
 */

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  HelpCircle,
  Plus,
} from "lucide-react";
import type { DiscoveryResult, DiscoveredFlyer } from "@/lib/flyer-bucket/types";
import { AddAliasFromDiscoveryDialog } from "./AddAliasFromDiscoveryDialog";

interface DiscoveryResultsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  result: DiscoveryResult | null;
  onSelectFlyers?: (flyers: DiscoveredFlyer[]) => void;
  selectable?: boolean;
}

export function DiscoveryResultsDialog({
  open,
  onOpenChange,
  result,
  onSelectFlyers,
  selectable = false,
}: DiscoveryResultsDialogProps) {
  const [selectedPaths, setSelectedPaths] = useState<Set<string>>(new Set());
  const [aliasDialogOpen, setAliasDialogOpen] = useState(false);
  const [aliasFlyer, setAliasFlyer] = useState<DiscoveredFlyer | null>(null);

  if (!result) return null;

  const handleToggleSelect = (path: string) => {
    const newSelected = new Set(selectedPaths);
    if (newSelected.has(path)) {
      newSelected.delete(path);
    } else {
      newSelected.add(path);
    }
    setSelectedPaths(newSelected);
  };

  const handleSelectAll = () => {
    setSelectedPaths(new Set(result.eligible_flyers.map((f) => f.object_path)));
  };

  const handleClearSelection = () => {
    setSelectedPaths(new Set());
  };

  const handleConfirmSelection = () => {
    if (onSelectFlyers) {
      const selected = result.eligible_flyers.filter((f) =>
        selectedPaths.has(f.object_path)
      );
      onSelectFlyers(selected);
    }
    onOpenChange(false);
  };

  const handleAddAlias = (flyer: DiscoveredFlyer) => {
    setAliasFlyer(flyer);
    setAliasDialogOpen(true);
  };

  const renderFlyerRow = (flyer: DiscoveredFlyer, showSelect = false) => (
    <TableRow key={flyer.object_path}>
      {showSelect && (
        <TableCell className="w-10">
          <input
            type="checkbox"
            checked={selectedPaths.has(flyer.object_path)}
            onChange={() => handleToggleSelect(flyer.object_path)}
            className="h-4 w-4"
          />
        </TableCell>
      )}
      <TableCell className="font-mono text-xs max-w-[200px] truncate">
        {flyer.filename}
      </TableCell>
      <TableCell>
        {flyer.matched_merchant_id ? (
          <div className="flex items-center gap-1">
            <span>{flyer.matched_merchant_id}</span>
            <Badge variant="outline" className="text-xs">
              {flyer.match_method}
            </Badge>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">{flyer.merchant_key_raw}</span>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2"
              onClick={() => handleAddAlias(flyer)}
            >
              <Plus className="h-3 w-3 mr-1" />
              Add alias
            </Button>
          </div>
        )}
      </TableCell>
      <TableCell>
        <Badge variant="secondary">{flyer.flyer_type_normalized}</Badge>
      </TableCell>
      <TableCell className="text-xs">
        {flyer.start_date} → {flyer.end_date}
      </TableCell>
      <TableCell>
      {flyer.errors.length > 0 ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <AlertTriangle className="h-4 w-4 text-destructive" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                {flyer.errors.map((e, i) => (
                  <p key={i} className="text-xs">{e}</p>
                ))}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <CheckCircle2 className="h-4 w-4 text-primary" />
        )}
      </TableCell>
    </TableRow>
  );

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Discovery Results</DialogTitle>
            <DialogDescription>
              Discovered {result.summary.total_files} files from{" "}
              <code className="text-xs bg-muted px-1 rounded">{result.bucket_name}</code>
              {result.prefix && (
                <span> / <code className="text-xs bg-muted px-1 rounded">{result.prefix}</code></span>
              )}
            </DialogDescription>
          </DialogHeader>

          {/* Summary Stats */}
          <div className="flex items-center gap-4 py-2">
            <Badge variant="default" className="gap-1">
              <CheckCircle2 className="h-3 w-3" />
              {result.summary.eligible_count} Eligible
            </Badge>
            <Badge variant="secondary" className="gap-1">
              <Clock className="h-3 w-3" />
              {result.summary.expired_count} Expired
            </Badge>
            <Badge variant="secondary" className="gap-1">
              <Clock className="h-3 w-3" />
              {result.summary.future_blocked_count} Future Blocked
            </Badge>
            <Badge variant="destructive" className="gap-1">
              <XCircle className="h-3 w-3" />
              {result.summary.invalid_format_count} Invalid
            </Badge>
            <Badge variant="outline" className="gap-1">
              <HelpCircle className="h-3 w-3" />
              {result.summary.unmatched_merchant_count} Unmatched
            </Badge>
          </div>

          {/* Tabs */}
          <Tabs defaultValue="eligible" className="flex-1 min-h-0">
            <TabsList>
              <TabsTrigger value="eligible">
                Eligible ({result.eligible_flyers.length})
              </TabsTrigger>
              <TabsTrigger value="unmatched">
                Unmatched ({result.unmatched_merchants.length})
              </TabsTrigger>
              <TabsTrigger value="expired">
                Expired ({result.expired_flyers.length})
              </TabsTrigger>
              <TabsTrigger value="future">
                Future ({result.future_blocked_flyers.length})
              </TabsTrigger>
              <TabsTrigger value="invalid">
                Invalid ({result.invalid_format.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="eligible" className="flex-1 min-h-0">
              {selectable && result.eligible_flyers.length > 0 && (
                <div className="flex items-center gap-2 py-2">
                  <Button variant="outline" size="sm" onClick={handleSelectAll}>
                    Select All
                  </Button>
                  <Button variant="ghost" size="sm" onClick={handleClearSelection}>
                    Clear
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    {selectedPaths.size} selected
                  </span>
                </div>
              )}
              <ScrollArea className="h-[300px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {selectable && <TableHead className="w-10" />}
                      <TableHead>Filename</TableHead>
                      <TableHead>Merchant</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Dates</TableHead>
                      <TableHead className="w-10" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.eligible_flyers.map((f) => renderFlyerRow(f, selectable))}
                    {result.eligible_flyers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={selectable ? 6 : 5} className="text-center text-muted-foreground py-8">
                          No eligible flyers found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </TabsContent>

            <TabsContent value="unmatched" className="flex-1 min-h-0">
              <ScrollArea className="h-[300px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Filename</TableHead>
                      <TableHead>Merchant (raw)</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Dates</TableHead>
                      <TableHead className="w-10" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.unmatched_merchants.map((f) => renderFlyerRow(f))}
                    {result.unmatched_merchants.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                          All merchants matched successfully
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </TabsContent>

            <TabsContent value="expired" className="flex-1 min-h-0">
              <ScrollArea className="h-[300px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Filename</TableHead>
                      <TableHead>Merchant</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Dates</TableHead>
                      <TableHead className="w-10" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.expired_flyers.map((f) => renderFlyerRow(f))}
                    {result.expired_flyers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                          No expired flyers
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </TabsContent>

            <TabsContent value="future" className="flex-1 min-h-0">
              <ScrollArea className="h-[300px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Filename</TableHead>
                      <TableHead>Merchant</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Dates</TableHead>
                      <TableHead className="w-10" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.future_blocked_flyers.map((f) => renderFlyerRow(f))}
                    {result.future_blocked_flyers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                          No future-blocked flyers
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </TabsContent>

            <TabsContent value="invalid" className="flex-1 min-h-0">
              <ScrollArea className="h-[300px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Path</TableHead>
                      <TableHead>Error</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.invalid_format.map((f, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-mono text-xs">{f.path}</TableCell>
                        <TableCell className="text-destructive text-sm">{f.error}</TableCell>
                      </TableRow>
                    ))}
                    {result.invalid_format.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={2} className="text-center text-muted-foreground py-8">
                          All files have valid format
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </TabsContent>
          </Tabs>

          {/* Footer with selection confirmation */}
          {selectable && (
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleConfirmSelection}
                disabled={selectedPaths.size === 0}
              >
                Use Selected ({selectedPaths.size})
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AddAliasFromDiscoveryDialog
        open={aliasDialogOpen}
        onOpenChange={setAliasDialogOpen}
        flyer={aliasFlyer}
      />
    </>
  );
}
